import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import "react-datepicker/dist/react-datepicker.css";
import { useMsal } from "@azure/msal-react";
import { toast } from "react-toastify";
import ProjectSheetService from "../Services/ProjectSheetService";
import ProjectSheetModel from "../Models/ProjectSheetModel";
import Checkbox from "./Checkbox";
import ReminderModal from "./ReminderModal";
import { format } from "date-fns";
import { User } from "@microsoft/microsoft-graph-types";
import GraphService from "../Services/GraphService";

interface ProjectSheetModalProps {
  projectCode: string;
  afterClose: Function;
}

const ProjectSheetModal = (props: ProjectSheetModalProps) => {
  const { instance } = useMsal();

  const projectSheetService = useMemo(
    () => new ProjectSheetService(instance),
    [instance]
  );
  const graphService = useMemo(() => new GraphService(instance), [instance]);

  const [visible, setVisible] = useState<boolean>(true);
  const [projectSheet, setProjectSheet] = useState<ProjectSheetModel>({
    projectCode: props.projectCode,
  });

  const [currentUser, setCurrentUser] = useState<User>();
  const [showReminderModal, setShowReminderModal] = useState<boolean>(false);

  const loadData = useCallback(
    async (projectCode: string) => {
      try {
        const data = await projectSheetService.get(projectCode);
        setProjectSheet(data);
      } catch (err) {
        console.log(err);
        toast.error("Fout bij het ophalen van de data.");
      } finally {
      }
    },
    [projectSheetService]
  );

  const loadCurrentUser = useCallback(async () => {
    try {
      const user = await graphService.getUserDetails();
      setCurrentUser(user);
    } catch (err) {
      console.log(err);
      toast.error("Fout bij het ophalen van de huidige gebruiker.");
    } finally {
    }
  }, [graphService]);

  useEffect(() => {
    loadData(props.projectCode);
  }, [props.projectCode]);

  useEffect(() => {
    loadCurrentUser();
  }, []);

  function modalToggle() {
    setVisible(!visible);
    props.afterClose();
  }

  async function modalSave() {
    try {
      if (projectSheet) {
        await projectSheetService.upsert(projectSheet);

        setVisible(false);
        props.afterClose();
      }
    } catch (err) {
      toast.error("Fout bij het bewaren van de data.");
    }
  }

  function reminderModalAfterClose() {
    setShowReminderModal(false);
  }

  const renderReminderModal = () => {
    return (
      <ReminderModal
        code={props.projectCode}
        reminder={{
          projectCode: props.projectCode,
          completed: false,
          personId: currentUser?.id,
          date: format(new Date(), "yyyy-MM-dd") + "T00:00:00.000Z",
        }}
        afterClose={() => reminderModalAfterClose()}
      />
    );
  };

  return (
    <>
      <Modal isOpen={visible} toggle={modalToggle} className="mw-100 w-75">
        <ModalHeader toggle={modalToggle}>
          Projectfiche - {props.projectCode}
        </ModalHeader>
        <ModalBody>
          <form>
            <div className="form-group">
              <Checkbox
                className=""
                label="BL aanwezig"
                value="true"
                checked={projectSheet.blPresent ?? false}
                onChange={(e: any) =>
                  setProjectSheet((pr) => {
                    return { ...pr, blPresent: e.target.checked };
                  })
                }
              />
            </div>
            <div className="form-group row">
              <label className="col-sm-2">Meer Info in OneNote</label>
              <div className="col-sm-10">
                <input
                  type="text"
                  className="form-control "
                  value={projectSheet.oneNoteUrl}
                  placeholder="plaats hier link naar pagina in one note"
                  onChange={(e) =>
                    setProjectSheet((pr) => {
                      return { ...pr, oneNoteUrl: e.target.value };
                    })
                  }
                />
              </div>
            </div>
            <div className="form-group">
              <Checkbox
                className=""
                label="Kleuradvies aan te vragen"
                value="true"
                checked={projectSheet.colourAdviceToRequest ?? false}
                onChange={(e: any) =>
                  setProjectSheet((pr) => {
                    return { ...pr, colourAdviceToRequest: e.target.checked };
                  })
                }
              />
            </div>
            <div className="form-group">
              <Checkbox
                className=""
                label="Kleuradvies aangevraagd"
                value="true"
                checked={projectSheet.colourAdviceRequested ?? false}
                onChange={(e: any) =>
                  setProjectSheet((pr) => {
                    return { ...pr, colourAdviceRequested: e.target.checked };
                  })
                }
              />
            </div>
            <div className="form-group">
              <Checkbox
                className=""
                label="Kleuradvies beschikbaar (in bestanden)"
                value="true"
                checked={projectSheet.colourAdviceAvailable ?? false}
                onChange={(e: any) =>
                  setProjectSheet((pr) => {
                    return { ...pr, colourAdviceAvailable: e.target.checked };
                  })
                }
              />
            </div>
            <div className="form-group">
              <Checkbox
                className=""
                label="Behang/lijsten/speciale produkten bestellen (hier vermelden)"
                value="true"
                checked={projectSheet.orderProducts ?? false}
                onChange={(e: any) =>
                  setProjectSheet((pr) => {
                    return { ...pr, orderProducts: e.target.checked };
                  })
                }
              />
              <textarea
                className="form-control "
                title="Opmerkingen"
                style={{ height: "100px" }}
                value={projectSheet.productsToOrder}
                onChange={(e) =>
                  setProjectSheet((pr) => {
                    return { ...pr, productsToOrder: e.target.value };
                  })
                }
              ></textarea>
            </div>
            <div className="form-group">
              <Checkbox
                className=""
                label="Foto’s beschikbaar (in bestanden of in onze note)"
                value="true"
                checked={projectSheet.photosAvailable ?? false}
                onChange={(e: any) =>
                  setProjectSheet((pr) => {
                    return { ...pr, photosAvailable: e.target.checked };
                  })
                }
              />
            </div>
            <div className="form-group">
              <b>Benodigde zaken te voorzien voor de werf</b>
            </div>
            <div className="form-group row">
              <div className="col-sm-3">
                <Checkbox
                  className=""
                  label="Hoogtewerker nodig"
                  value="true"
                  checked={projectSheet.awpNeeded ?? false}
                  onChange={(e: any) =>
                    setProjectSheet((pr) => {
                      return { ...pr, awpNeeded: e.target.checked };
                    })
                  }
                />
              </div>
              <div className="col-sm-9 row">
                <label className="col-sm-2">welke hoogte</label>
                <div className="col">
                  <input
                    type="text"
                    className="form-control "
                    value={projectSheet.awpHeight}
                    onChange={(e) =>
                      setProjectSheet((pr) => {
                        return { ...pr, awpHeight: e.target.value };
                      })
                    }
                  />
                </div>
              </div>
            </div>
            <div className="form-group row">
              <div className="col-sm-3">
                <Checkbox
                  className=""
                  label="Kamerstelling nodig"
                  value="true"
                  checked={projectSheet.roomScaffoldNeeded ?? false}
                  onChange={(e: any) =>
                    setProjectSheet((pr) => {
                      return { ...pr, roomScaffoldNeeded: e.target.checked };
                    })
                  }
                />
              </div>
              <div className="col-sm-9 row">
                <label className="col-sm-2">welke</label>
                <div className="col">
                  <input
                    type="text"
                    className="form-control "
                    value={projectSheet.roomScaffoldDescription}
                    onChange={(e) =>
                      setProjectSheet((pr) => {
                        return {
                          ...pr,
                          roomScaffoldDescription: e.target.value,
                        };
                      })
                    }
                  />
                </div>
              </div>
            </div>
            <div className="form-group">
              <Checkbox
                className=""
                label="Loopplank nodig"
                value="true"
                checked={projectSheet.gangwayNeeded ?? false}
                onChange={(e: any) =>
                  setProjectSheet((pr) => {
                    return { ...pr, gangwayNeeded: e.target.checked };
                  })
                }
              />
            </div>
            <div className="form-group">
              <Checkbox
                className=""
                label="Steekladder nodig"
                value="true"
                checked={projectSheet.stepLadderNeeded ?? false}
                onChange={(e: any) =>
                  setProjectSheet((pr) => {
                    return { ...pr, stepLadderNeeded: e.target.checked };
                  })
                }
              />
            </div>
            <div className="form-group">
              <Checkbox
                className=""
                label="Gevelstelling huren"
                value="true"
                checked={projectSheet.facadeScaffoldNeeded ?? false}
                onChange={(e: any) =>
                  setProjectSheet((pr) => {
                    return { ...pr, facadeScaffoldNeeded: e.target.checked };
                  })
                }
              />
            </div>
            <div className="form-group">
              <label className="my-1 mr-2">Opmerkingen</label>
              <textarea
                className="form-control "
                title="Opmerkingen"
                style={{ height: "100px" }}
                value={projectSheet.remarks}
                onChange={(e) =>
                  setProjectSheet((pr) => {
                    return { ...pr, remarks: e.target.value };
                  })
                }
              ></textarea>
            </div>
            <div className="form-group">
              <Checkbox
                className=""
                label="Voorbereid"
                value="true"
                checked={projectSheet.ready ?? false}
                onChange={(e: any) =>
                  setProjectSheet((pr) => {
                    return { ...pr, ready: e.target.checked };
                  })
                }
              />
            </div>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            onClick={() => {
              setShowReminderModal(true);
            }}
          >
            Nieuwe reminder
          </Button>{" "}
          <Button color="primary" onClick={modalSave}>
            Bewaren
          </Button>{" "}
          <Button color="secondary" onClick={modalToggle}>
            Sluiten
          </Button>
        </ModalFooter>
      </Modal>
      {showReminderModal && renderReminderModal()}
    </>
  );
};

export default ProjectSheetModal;
